/**
 * subscriptionSyncService
 *
 * Coordinates safe, rate-limited Stripe subscription syncs with the backend.
 * Skips syncs for enterprise users, queues concurrent requests, and exposes
 * helpers for sync state and timing.
 */
import { post } from '../api/core';

// Keep track of pending syncs to prevent multiple simultaneous syncs
let isSyncing = false;
let lastSyncTime = null;
let syncQueue = [];

// Rate limiting constants
const MIN_SYNC_INTERVAL = 30000; // 30 seconds minimum between syncs
const FORCE_SYNC_INTERVAL = 300000; // 5 minutes for force sync

/**
 * Check if enough time has passed since last sync
 * @param {boolean} force - Whether to force sync regardless of time
 * @returns {boolean} True if sync should be allowed
 */
const shouldAllowSync = (force = false) => {
  if (!lastSyncTime) return true; // First sync always allowed
  
  const timeSinceLastSync = Date.now() - lastSyncTime.getTime();
  
  if (force) {
    return timeSinceLastSync >= FORCE_SYNC_INTERVAL;
  }
  
  return timeSinceLastSync >= MIN_SYNC_INTERVAL;
};

/**
 * Synchronizes the user's subscription data with Stripe
 * @param {Function} onComplete - Optional callback function when sync completes
 * @param {boolean} force - Whether to force sync regardless of rate limiting
 * @param {Object} user - Optional user object to check for enterprise status
 * @returns {Promise<Object>} Result of the synchronization
 */
export const syncSubscriptionData = async (onComplete, force = false, user = null) => {
  // Check if user is enterprise - if so, skip Stripe sync entirely
  if (user?.isEnterpriseUser || user?.isEnterprise) {
    console.log('Subscription sync skipped - user is enterprise and does not use Stripe billing');
    const enterpriseResult = {
      synced: false,
      skipped: true,
      reason: 'enterprise_user',
      message: 'Enterprise users do not use Stripe billing'
    };
    
    if (onComplete && typeof onComplete === 'function') {
      onComplete(enterpriseResult);
    }
    
    return enterpriseResult;
  }

  // Check rate limiting first
  if (!shouldAllowSync(force)) {
    const timeSinceLastSync = Date.now() - lastSyncTime.getTime();
    const timeUntilNextSync = MIN_SYNC_INTERVAL - timeSinceLastSync;
    
    console.log(`Subscription sync rate limited. Last sync was ${Math.round(timeSinceLastSync / 1000)}s ago. Next sync allowed in ${Math.round(timeUntilNextSync / 1000)}s`);
    
    // Return the last sync result if available, or a cached response
    if (onComplete && typeof onComplete === 'function') {
      onComplete({ 
        synced: false, 
        cached: true, 
        message: 'Rate limited - using cached data',
        lastSyncTime: lastSyncTime 
      });
    }
    
    return { 
      synced: false, 
      cached: true, 
      message: 'Rate limited - using cached data',
      lastSyncTime: lastSyncTime 
    };
  }
  
  // If a sync is already in progress, add to queue and return
  if (isSyncing) {
    console.log('Subscription sync already in progress, adding to queue');
    return new Promise((resolve, reject) => {
      syncQueue.push({ resolve, reject, onComplete });
    });
  }
  
  try {
    isSyncing = true;
    console.log('Syncing subscription data with Stripe');
    
    const response = await post('/stripe/sync-subscription');
    console.log('Subscription sync response:', response);
    
    // Record last sync time
    lastSyncTime = new Date();
    
    // Add sync metadata to response
    const enhancedResponse = {
      ...response,
      synced: true,
      syncTime: lastSyncTime
    };
    
    // Call onComplete callback if provided
    if (onComplete && typeof onComplete === 'function') {
      onComplete(enhancedResponse);
    }
    
    // Process queue after current sync completes
    processQueue(enhancedResponse);
    
    return enhancedResponse;
  } catch (err) {
    console.error('Error syncing subscription:', err);
    
    // Process queue with error
    processQueue(null, err);
    
    throw err;
  } finally {
    isSyncing = false;
  }
};

/**
 * Process any queued sync requests
 * @param {Object} result - The result from the current sync
 * @param {Error} error - Any error that occurred
 */
const processQueue = (result, error) => {
  if (syncQueue.length === 0) return;
  
  console.log(`Processing ${syncQueue.length} queued subscription sync requests`);
  
  // Process all items in the queue with the same result
  const currentQueue = [...syncQueue];
  syncQueue = []; // Clear the queue
  
  currentQueue.forEach(item => {
    try {
      if (error) {
        item.reject(error);
      } else {
        // Call onComplete callback if provided
        if (item.onComplete && typeof item.onComplete === 'function') {
          item.onComplete(result);
        }
        item.resolve(result);
      }
    } catch (err) {
      console.error('Error processing queued sync request:', err);
    }
  });
};

/**
 * Get the last sync time
 * @returns {Date|null} The timestamp of the last sync
 */
export const getLastSyncTime = () => {
  return lastSyncTime;
};

/**
 * Check if a sync is currently in progress
 * @returns {boolean} True if sync is in progress
 */
export const isSyncInProgress = () => {
  return isSyncing;
};

/**
 * Check if sync is allowed based on rate limiting
 * @param {boolean} force - Whether to check for force sync interval
 * @returns {boolean} True if sync is allowed
 */
export const isSyncAllowed = (force = false) => {
  return shouldAllowSync(force);
};

export default {
  syncSubscriptionData,
  getLastSyncTime,
  isSyncInProgress,
  isSyncAllowed
}; 